import { useEffect, useMemo, useState } from 'react'
import {Plus, Trash2, ArrowUp, ArrowDown, Save, RefreshCw, Scale, FileText} from 'lucide-react'
import toast from 'react-hot-toast'
import RichTextEditor from '@/components/RichTextEditor'
import { useAuth } from '@/hooks/useAuth'
import {
  useLegalPages,
  type LegalPageKey,
  type LegalSection,
} from '@/hooks/useLegalPages'

// Contenu statique de référence (fallback) — synchronisé avec LegalPage.tsx
const STATIC_LEGAL: Record<
  LegalPageKey,
  { title: string; h1: string; metaDescription: string; sections: LegalSection[] }
> = {
  'mentions-legales': {
    title: 'Mentions légales — Emplois BNSSA & MNS',
    h1: 'Mentions Légales',
    metaDescription:
      "Mentions légales du site Emplois BNSSA & MNS : éditeur, hébergeur, propriété intellectuelle et responsabilité.",
    sections: [
      {
        title: 'Éditeur du site',
        content:
          "<p>Le site <strong>Emplois BNSSA &amp; MNS</strong> est édité par la société Emplois BNSSA &amp; MNS SAS, au capital de 10 000 €, immatriculée au RCS de Paris sous le numéro 900 000 000. Siège social : 10 rue des Piscines, 75001 Paris, France. Email : contact@bnssa-mns.fr. Téléphone : 01 00 00 00 00.</p>",
      },
      {
        title: 'Directeur de la publication',
        content:
          '<p>Le directeur de la publication est M. Le Président de la société Emplois BNSSA &amp; MNS SAS.</p>',
      },
      {
        title: 'Hébergement',
        content:
          '<p>Le site est hébergé par OVH SAS, 2 rue Kellermann, 59100 Roubaix, France. Téléphone : 09 72 10 10 07.</p>',
      },
      {
        title: 'Propriété intellectuelle',
        content:
          "<p>L'ensemble du contenu du site (textes, images, logos, vidéos) est la propriété exclusive d'Emplois BNSSA &amp; MNS ou de ses partenaires. Toute reproduction, totale ou partielle, est strictement interdite sans autorisation écrite préalable.</p>",
      },
      {
        title: 'Responsabilité',
        content:
          "<p>Emplois BNSSA &amp; MNS s'efforce d'assurer l'exactitude des informations diffusées sur le site. Toutefois, l'éditeur ne saurait être tenu responsable des erreurs, omissions ou résultats obtenus à partir de l'utilisation des informations.</p>",
      },
    ],
  },
  'politique-confidentialite': {
    title: 'Politique de confidentialité — Emplois BNSSA & MNS',
    h1: 'Politique de Confidentialité',
    metaDescription:
      'Politique de confidentialité et protection des données personnelles du site Emplois BNSSA & MNS (RGPD).',
    sections: [
      {
        title: 'Collecte des données personnelles',
        content:
          '<p>Nous collectons des données personnelles lors de votre inscription (nom, email, téléphone, ville, diplômes), lors de vos candidatures (CV, lettre de motivation) et lors de votre navigation (cookies, adresse IP). Ces données sont strictement nécessaires à la fourniture du service.</p>',
      },
      {
        title: 'Finalité du traitement',
        content:
          '<p>Vos données sont utilisées pour : créer et gérer votre compte, mettre en relation candidats et recruteurs, personnaliser les offres proposées, envoyer des notifications et alertes emploi, améliorer nos services.</p>',
      },
      {
        title: 'Vos droits (RGPD)',
        content:
          "<p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité, de limitation et d'opposition concernant vos données personnelles. Pour exercer ces droits, contactez : <a href=\"mailto:dpo@bnssa-mns.fr\">dpo@bnssa-mns.fr</a>.</p>",
      },
      {
        title: 'Cookies',
        content:
          '<p>Le site utilise des cookies techniques et analytiques. Vous pouvez à tout moment désactiver les cookies via les paramètres de votre navigateur. Certains cookies sont indispensables au fonctionnement du site.</p>',
      },
      {
        title: 'Conservation des données',
        content:
          '<p>Vos données sont conservées pendant la durée de votre inscription et jusqu\'à 3 ans après votre dernière connexion. Au-delà, elles sont anonymisées ou supprimées.</p>',
      },
    ],
  },
  cgv: {
    title: 'CGU / CGV — Emplois BNSSA & MNS',
    h1: "Conditions Générales d'Utilisation et de Vente",
    metaDescription:
      "Conditions Générales d'Utilisation et de Vente du site Emplois BNSSA & MNS : inscription, obligations, tarifs et droit applicable.",
    sections: [
      {
        title: 'Objet',
        content:
          "<p>Les présentes CGU/CGV régissent l'utilisation du site Emplois BNSSA &amp; MNS, plateforme de mise en relation entre candidats (BNSSA, MNS) et recruteurs du secteur des activités aquatiques.</p>",
      },
      {
        title: 'Inscription et compte',
        content:
          "<p>L'inscription sur le site est gratuite pour les candidats. Les recruteurs peuvent publier leur première offre gratuitement, puis souscrire à un abonnement pour les offres suivantes. Chaque utilisateur s'engage à fournir des informations exactes et à jour.</p>",
      },
      {
        title: 'Obligations du candidat',
        content:
          "<p>Le candidat s'engage à fournir des informations véridiques sur son profil, ses diplômes et son expérience. La fourniture de fausses informations peut entraîner la suspension immédiate du compte.</p>",
      },
      {
        title: 'Obligations du recruteur',
        content:
          "<p>Le recruteur s'engage à publier des offres réelles, conformes au droit du travail et à la réglementation des métiers aquatiques. Toute offre discriminatoire ou frauduleuse sera supprimée.</p>",
      },
      {
        title: 'Tarifs',
        content:
          '<p>Les tarifs des offres premium pour recruteurs sont indiqués sur la page tarifs et facturés en euros TTC. Les paiements sont sécurisés. La première offre est offerte aux nouveaux recruteurs.</p>',
      },
      {
        title: 'Limitation de responsabilité',
        content:
          "<p>Emplois BNSSA &amp; MNS agit en tant qu'intermédiaire et ne peut être tenu responsable des relations contractuelles entre candidats et recruteurs.</p>",
      },
      {
        title: 'Droit applicable',
        content:
          '<p>Les présentes CGU/CGV sont régies par le droit français. Tout litige relèvera des tribunaux compétents de Paris.</p>',
      },
    ],
  },
}

const PAGE_TABS: { key: LegalPageKey; label: string; path: string; icon: typeof Scale }[] = [
  { key: 'mentions-legales', label: 'Mentions légales', path: '/mentions-legales', icon: FileText },
  { key: 'politique-confidentialite', label: 'Politique de confidentialité', path: '/politique-confidentialite', icon: Scale },
  { key: 'cgv', label: 'CGU / CGV', path: '/cgv', icon: FileText },
]

interface EditState {
  title: string
  h1: string
  metaDescription: string
  sections: LegalSection[]
}

export default function LegalEditor() {
  const { user } = useAuth()
  const { docs, loading, error, fetchAll, savePage, getByKey } = useLegalPages()
  const [activeKey, setActiveKey] = useState<LegalPageKey>('mentions-legales')
  const [editState, setEditState] = useState<EditState | null>(null)
  const [saving, setSaving] = useState(false)

  // Charge l'état d'édition à chaque changement d'onglet ou nouveau fetch
  useEffect(() => {
    const override = getByKey(activeKey)
    const base = STATIC_LEGAL[activeKey]
    if (override) {
      setEditState({
        title: override.title || base.title,
        h1: override.h1 || base.h1,
        metaDescription: override.metaDescription || base.metaDescription,
        sections:
          Array.isArray(override.sections) && override.sections.length > 0
            ? override.sections.map((s) => ({ title: s.title || '', content: s.content || '' }))
            : base.sections.map((s) => ({ ...s })),
      })
    } else {
      setEditState({
        title: base.title,
        h1: base.h1,
        metaDescription: base.metaDescription,
        sections: base.sections.map((s) => ({ ...s })),
      })
    }
  }, [activeKey, docs, getByKey])

  const currentTab = useMemo(
    () => PAGE_TABS.find((t) => t.key === activeKey) ?? PAGE_TABS[0],
    [activeKey],
  )

  const isOverridden = !!getByKey(activeKey)

  const updateField = <K extends keyof EditState>(key: K, value: EditState[K]) => {
    setEditState((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  const updateSection = (index: number, patch: Partial<LegalSection>) => {
    setEditState((prev) => {
      if (!prev) return prev
      const next = prev.sections.slice()
      next[index] = { ...next[index], ...patch }
      return { ...prev, sections: next }
    })
  }

  const addSection = () => {
    setEditState((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        sections: [...prev.sections, { title: 'Nouvelle section', content: '<p></p>' }],
      }
    })
  }

  const removeSection = (index: number) => {
    if (!window.confirm('Supprimer cette section ?')) return
    setEditState((prev) => {
      if (!prev) return prev
      return { ...prev, sections: prev.sections.filter((_, i) => i !== index) }
    })
  }

  const moveSection = (index: number, direction: 'up' | 'down') => {
    setEditState((prev) => {
      if (!prev) return prev
      const next = prev.sections.slice()
      const target = direction === 'up' ? index - 1 : index + 1
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return { ...prev, sections: next }
    })
  }

  const resetToStatic = () => {
    if (!window.confirm('Réinitialiser le contenu de cette page avec la version par défaut ? Vos modifications non sauvegardées seront perdues.')) return
    const base = STATIC_LEGAL[activeKey]
    setEditState({
      title: base.title,
      h1: base.h1,
      metaDescription: base.metaDescription,
      sections: base.sections.map((s) => ({ ...s })),
    })
  }

  const handleSave = async () => {
    if (!editState) return
    if (!editState.h1.trim() || !editState.title.trim()) {
      toast.error('Le titre et le H1 sont obligatoires.')
      return
    }
    if (editState.sections.length === 0) {
      toast.error('Ajoutez au moins une section.')
      return
    }
    if (editState.sections.some((s) => !s.title.trim())) {
      toast.error('Chaque section doit avoir un titre.')
      return
    }
    setSaving(true)
    try {
      await savePage(
        {
          pageKey: activeKey,
          title: editState.title.trim(),
          h1: editState.h1.trim(),
          metaDescription: editState.metaDescription.trim(),
          sections: editState.sections.map((s) => ({
            title: s.title.trim(),
            content: s.content || '',
          })),
        },
        user?.userId ?? 'admin',
      )
      toast.success('Page sauvegardée.')
    } catch (err) {
      console.error('[LegalEditor] save failed:', err)
      toast.error('La sauvegarde a échoué.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Scale className="w-4 h-4 text-gray-700" />
            Pages légales
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Editez le contenu publié sur <code className="text-[11px] bg-gray-100 px-1 py-0.5 rounded">/mentions-legales</code>,{' '}
            <code className="text-[11px] bg-gray-100 px-1 py-0.5 rounded">/politique-confidentialite</code> et{' '}
            <code className="text-[11px] bg-gray-100 px-1 py-0.5 rounded">/cgv</code>.
          </p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 disabled:opacity-50 self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-xl p-2 flex flex-wrap gap-1">
        {PAGE_TABS.map((t) => {
          const Icon = t.icon
          const active = activeKey === t.key
          const overridden = !!getByKey(t.key)
          return (
            <button
              key={t.key}
              onClick={() => setActiveKey(t.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                active
                  ? 'bg-gray-900 text-white'
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              {overridden && (
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    active ? 'bg-white text-gray-900' : 'bg-emerald-100 text-emerald-800'
                  }`}
                  title="Override personnalisé enregistré"
                >
                  Personnalisé
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Editor */}
      {editState && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  isOverridden ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {isOverridden ? 'Contenu personnalisé actif' : 'Contenu par défaut'}
              </span>
              <a
                href={currentTab.path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline"
              >
                Voir la page publique →
              </a>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetToStatic}
                className="px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Réinitialiser
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-md hover:bg-gray-800 disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
            </div>
          </div>

          {/* Métadonnées */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Titre (balise &lt;title&gt;)
              </label>
              <input
                type="text"
                value={editState.title}
                onChange={(e) => updateField('title', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                placeholder="Titre SEO"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Titre principal (H1)
              </label>
              <input
                type="text"
                value={editState.h1}
                onChange={(e) => updateField('h1', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                placeholder="H1 affiché sur la page"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Meta description SEO
              </label>
              <textarea
                value={editState.metaDescription}
                onChange={(e) => updateField('metaDescription', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                placeholder="Description courte (150–160 caractères)"
              />
              <p className="mt-1 text-[11px] text-gray-500">
                {editState.metaDescription.length} caractères
              </p>
            </div>
          </div>

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-gray-900">
                Sections ({editState.sections.length})
              </h3>
              <button
                type="button"
                onClick={addSection}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-emerald-600 rounded-md hover:bg-emerald-700"
              >
                <Plus className="w-3.5 h-3.5" />
                Ajouter une section
              </button>
            </div>

            <div className="space-y-4">
              {editState.sections.map((section, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50/40"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-900 text-white text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => updateSection(index, { title: e.target.value })}
                      placeholder="Titre de la section"
                      className="flex-1 px-3 py-1.5 text-sm font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 bg-white"
                    />
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveSection(index, 'up')}
                        disabled={index === 0}
                        title="Monter"
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-white rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSection(index, 'down')}
                        disabled={index === editState.sections.length - 1}
                        title="Descendre"
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-white rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSection(index)}
                        title="Supprimer"
                        className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <RichTextEditor
                    value={section.content}
                    onChange={(html) => updateSection(index, { content: html })}
                    placeholder="Contenu de la section…"
                    minHeight={140}
                  />
                </div>
              ))}
            </div>

            {editState.sections.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg">
                Aucune section. Cliquez sur « Ajouter une section » pour commencer.
              </div>
            )}
          </div>

          {/* Save bottom */}
          <div className="pt-3 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-md hover:bg-gray-800 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Sauvegarde…' : 'Sauvegarder la page'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
